import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faHourglassClock } from '@fortawesome/pro-regular-svg-icons/faHourglassClock';
import { faMugSaucer } from '@fortawesome/pro-regular-svg-icons/faMugSaucer';
import { faPlateUtensils } from '@fortawesome/pro-regular-svg-icons/faPlateUtensils';
import { faTimer } from '@fortawesome/pro-regular-svg-icons/faTimer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Col, Flex, Row, theme } from 'antd';
import dayjs from 'dayjs';
import PosText from 'pos-client/components/shared/General/PosText.tsx';
import { BLUE, DARK_GREY } from 'pos-client/utils/colours.ts';
import type { UserShiftModel } from 'pos-shared/models/userSchema.ts';

import type { DayName, UserShiftGroupedRow } from './UserShift';

type UserShiftCardProps = {
  shiftData: UserShiftModel[];
  weekData?: UserShiftGroupedRow;
  day: DayName;
};

const getTotalShiftTime = (items: UserShiftModel[]) => {
  const totalMinutes = items.reduce((sum, item) => {
    if (item.type === 'Shift') {
      return sum + dayjs(item.end).diff(dayjs(item.start), 'minute');
    }
    return sum;
  }, 0);

  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return totalMinutes > 0 ? `${h}h ${m}m` : null;
};

export const UserShiftCard = ({
  shiftData,
  weekData,
  day,
}: UserShiftCardProps) => {
  const { token } = theme.useToken();
  const weekDayDate = weekData?._dateMap?.[day];

  const durationString = getTotalShiftTime(shiftData);

  const shiftTime = shiftData.find((item) => item.type === 'Shift');
  const breakTime = shiftData.find((item) => item.type === 'Break');
  const mealTime = shiftData.find((item) => item.type === 'Meal');

  const offShift = !shiftData || shiftData.length === 0;

  return (
    <Flex
      vertical
      style={{
        padding: 0,
        border: `1px solid ${offShift ? DARK_GREY : BLUE.color}`,
        height: '160px',
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '8px',
      }}
    >
      <Col
        style={{
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          background: offShift ? 'lightgrey' : BLUE.background,
          borderBottom: `1px solid ${'black'}`,
          padding: token.paddingXS,
        }}
      >
        <Row justify="space-between" align="middle">
          <PosText>{weekDayDate}</PosText>
        </Row>
      </Col>
      <Flex
        vertical
        style={{
          padding: token.paddingXS,
        }}
      >
        {offShift ? (
          <ShiftCardDetail
            type={shiftTime?.type || 'Off Shift'}
            start={shiftTime?.start ?? ''}
            end={shiftTime?.end ?? ''}
            icon={faTimer}
            iconColor={DARK_GREY}
            offShift={offShift}
          />
        ) : (
          <>
            <ShiftCardDetail
              type={shiftTime?.type || 'Shift'}
              start={shiftTime?.start ?? ''}
              end={shiftTime?.end ?? ''}
              icon={faTimer}
              iconColor={DARK_GREY}
            />
            <ShiftCardDetail
              type={breakTime?.type || 'Break'}
              start={breakTime?.start ?? ''}
              end={breakTime?.end ?? ''}
              icon={faMugSaucer}
              iconColor={DARK_GREY}
            />
            <ShiftCardDetail
              type={mealTime?.type || 'Meal'}
              start={mealTime?.start ?? ''}
              end={mealTime?.end ?? ''}
              icon={faPlateUtensils}
              iconColor={DARK_GREY}
            />
          </>
        )}
        <Row style={{ display: 'flex', alignItems: 'center' }}>
          {offShift ? null : (
            <>
              <FontAwesomeIcon
                icon={faHourglassClock}
                style={{
                  color: DARK_GREY,
                  marginRight: token.marginXXS,
                }}
              />
              <Col>
                <strong>{'Duration'}</strong>: {durationString || '-'}
              </Col>
            </>
          )}
        </Row>
      </Flex>
    </Flex>
  );
};

type ShiftCardDetailProps = {
  type: string;
  start?: string | Date | null;
  end?: string | Date | null;
  icon?: IconProp;
  iconColor?: string;
  offShift?: boolean;
};

export const ShiftCardDetail = (props: ShiftCardDetailProps) => {
  const { token } = theme.useToken();
  const { type, icon, start, end, iconColor, offShift } = props;
  return (
    <Row
      style={{
        display: 'flex',
        alignItems: 'center',
        paddingBottom: token.paddingXXS,
      }}
    >
      {icon && (
        <FontAwesomeIcon
          icon={icon}
          style={{
            color: iconColor,
            marginRight: token.marginXXS,
          }}
        />
      )}
      {offShift ? (
        <Flex wrap="wrap">
          <strong>{type}</strong>
        </Flex>
      ) : (
        <Flex wrap="wrap" style={{ flex: 1 }}>
          <strong>{type}</strong>:{' '}
          {!start ? 'No data' : `${dayjs(start).utc().format('HH:mm')} - `}
          {!end ? '' : dayjs(end).utc().format('HH:mm')}
        </Flex>
      )}
    </Row>
  );
};
